import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { trpc } from '@/config/trpc'
import { useSharedView } from '@/context/sharedView'
import { ScheduledPostsCalendar } from '@/components/calendar/ScheduledPostsCalendar'
import { MyPostsList } from '@/components/my-posts/my-posts-list'
import { ImportPostsModal } from '@/components/my-posts/import-posts-modal'
import { ShareLinkDialog } from '@/components/my-posts/share-link-dialog'
import { usePostEditing } from '@/components/my-posts/use-post-editing'
import { Button } from '@/components/ui/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { Calendar as CalendarIcon, List, ChevronDown, Download, CalendarDays } from 'lucide-react'
import { getPlatformDisplayName } from '@/lib/calendar/types'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'

type ViewMode = 'list' | 'timeline' | 'calendar'
type StatusFilter = 'all' | 'scheduled' | 'published' | 'imported' | 'failed'
type PlatformFilter = 'all' | PublishablePlatformType

const VIEW_VALUES: ViewMode[] = ['list', 'timeline', 'calendar']
const STATUS_VALUES: StatusFilter[] = ['all', 'scheduled', 'published', 'imported', 'failed']
const PLATFORM_VALUES: PlatformFilter[] = [
  'all',
  'linkedin',
  'instagram',
  'tiktok',
  'youtube',
  'facebook',
]

const VIEW_META: Record<ViewMode, { label: string; Icon: typeof List }> = {
  list: { label: 'List', Icon: List },
  timeline: { label: 'Timeline', Icon: CalendarDays },
  calendar: { label: 'Calendar', Icon: CalendarIcon },
}

function readEnumParam<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const value = params.get(key)
  return (allowed as readonly string[]).includes(value ?? '') ? (value as T) : fallback
}

export const ScheduledPostsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = readEnumParam<ViewMode>(searchParams, 'view', VIEW_VALUES, 'calendar')
  const statusFilter = readEnumParam<StatusFilter>(searchParams, 'status', STATUS_VALUES, 'all')
  const platformFilter = readEnumParam<PlatformFilter>(
    searchParams,
    'platform',
    PLATFORM_VALUES,
    'all',
  )
  const [importModalOpen, setImportModalOpen] = useState(false)
  const { isShared } = useSharedView()
  const { selectEvent, modal } = usePostEditing({ forceReadOnly: isShared })
  const brandDisplay = trpc.brand.getDisplay.useQuery(undefined, { enabled: isShared })

  // Shared viewers can open the detail modal, but it's pinned read-only (see
  // usePostEditing's forceReadOnly) so no edit/delete controls or mutations are
  // reachable. Drag-to-reschedule stays a no-op — handled inside
  // ScheduledPostsCalendar via the same useSharedView hook.
  const handleSelectEvent = selectEvent

  const updateParam = (key: string, value: string, defaultValue: string) => {
    setSearchParams(
      (prev) => {
        if (value === defaultValue) {
          prev.delete(key)
        } else {
          prev.set(key, value)
        }
        return prev
      },
      { replace: true },
    )
  }

  const ViewIcon = VIEW_META[view].Icon

  return (
    <section className="mx-auto flex h-[100dvh] w-full max-w-5xl flex-col px-6 py-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-foreground text-xl font-semibold tracking-tight">My Posts</h1>
          {isShared && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              {brandDisplay.data
                ? `Shared by ${brandDisplay.data.name} · read-only`
                : 'Read-only view'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                {statusFilter === 'all' ? 'All Statuses' : capitalize(statusFilter)}
                <ChevronDown size={12} className="opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {STATUS_VALUES.map((s) => (
                <DropdownMenuItem
                  key={s}
                  onClick={() => updateParam('status', s, 'all')}
                  className={statusFilter === s ? 'bg-hover' : ''}>
                  {s === 'all' ? 'All Statuses' : capitalize(s)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                {platformFilter === 'all'
                  ? 'All Platforms'
                  : getPlatformDisplayName(platformFilter)}
                <ChevronDown size={12} className="opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {PLATFORM_VALUES.map((p) => (
                <DropdownMenuItem
                  key={p}
                  onClick={() => updateParam('platform', p, 'all')}
                  className={platformFilter === p ? 'bg-hover' : ''}>
                  {p === 'all' ? 'All Platforms' : getPlatformDisplayName(p)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="bg-border mx-1 h-5 w-px" />

          {!isShared && (
            <>
              <ShareLinkDialog />
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setImportModalOpen(true)}>
                <Download size={13} />
                Import
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <ViewIcon size={13} />
                {VIEW_META[view].label}
                <ChevronDown size={12} className="opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {VIEW_VALUES.map((v) => {
                const { label, Icon } = VIEW_META[v]
                return (
                  <DropdownMenuItem
                    key={v}
                    onClick={() => updateParam('view', v, 'calendar')}
                    className={view === v ? 'bg-hover' : ''}>
                    <Icon className="mr-2 h-4 w-4" />
                    {label}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {view === 'list' ? (
          <div className="flex-1 overflow-y-auto pr-1">
            <MyPostsList
              status={statusFilter}
              platform={platformFilter}
              onSelectEvent={handleSelectEvent}
              onImport={isShared ? () => {} : () => setImportModalOpen(true)}
            />
          </div>
        ) : (
          <ScheduledPostsCalendar
            view={view}
            status={statusFilter}
            platform={platformFilter}
            onSelectEvent={handleSelectEvent}
          />
        )}
      </div>

      {modal}
      {!isShared && <ImportPostsModal open={importModalOpen} onOpenChange={setImportModalOpen} />}
    </section>
  )
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
